import { create } from "zustand";

export type StatusType = "success" | "warning" | "error" | "info" | "loading";

export interface StatusBarState {
  message: string;
  type: StatusType;
  isVisible: boolean;
  icon: string;
  actions: {
    setSuccess: (message: string) => void;
    setWarning: (message: string) => void;
    setError: (message: string) => void;
    setInfo: (message: string) => void;
    setLoading: (message: string) => void;
    setCustom: (message: string, type: StatusType, icon?: string) => void;
    hide: () => void;
    show: () => void;
    reset: () => void;
    checkBackendConnection: () => Promise<void>;
    updateConnectionStatus: (isConnected: boolean, count?: number) => void;
  };
  isConnected: boolean;
  legalAuthoritiesCount: number;
  lastChecked: Date | null;
}

const getIconForType = (type: StatusType): string => {
  switch (type) {
    case "success":
      return "✓";
    case "warning":
      return "⚠";
    case "error":
      return "✗";
    case "info":
      return "ℹ";
    case "loading":
      return "⟳";
    default:
      return "✓";
  }
};

const getColorForType = (type: StatusType): string => {
  switch (type) {
    case "success":
      return "bg-command-dark-green/80";
    case "warning":
      return "bg-yellow-500";
    case "error":
      return "bg-red-500";
    case "info":
      return "bg-blue-500";
    case "loading":
      return "bg-command-light-blue";
    default:
      return "bg-command-dark-green/80";
  }
};

export const useStatusBarStore = create<StatusBarState>((set, get) => ({
  message: "Command Legacy Beta: We don’t track, we back.",
  type: "success",
  isVisible: true,
  icon: "✓",
  isConnected: true,
  legalAuthoritiesCount: 16,
  lastChecked: null,

  actions: {
    setSuccess: (message: string) =>
      set({
        message,
        type: "success",
        icon: getIconForType("success"),
        isVisible: true,
      }),

    setWarning: (message: string) =>
      set({
        message,
        type: "warning",
        icon: getIconForType("warning"),
        isVisible: true,
      }),

    setError: (message: string) =>
      set({
        message,
        type: "error",
        icon: getIconForType("error"),
        isVisible: true,
      }),

    setInfo: (message: string) =>
      set({
        message,
        type: "info",
        icon: getIconForType("info"),
        isVisible: true,
      }),

    setLoading: (message: string) =>
      set({
        message,
        type: "loading",
        icon: getIconForType("loading"),
        isVisible: true,
      }),

    setCustom: (message: string, type: StatusType, icon?: string) =>
      set({
        message,
        type,
        icon: icon || getIconForType(type),
        isVisible: true,
      }),

    hide: () => set({ isVisible: false }),

    show: () => set({ isVisible: true }),

    reset: () => {
      const state = get();
      set({
        message: "Command Legacy Beta: We don’t track, we back.",
        type: "success",
        icon: "✓",
        isVisible: true,
      });
    },

    checkBackendConnection: async () => {
      try {
        set({ 
          message: "Checking system status...", 
          type: "loading", 
          icon: "⟳" 
        });

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/discourse/health`);
        
        if (response.ok) {
          const data = await response.json();
          const count = data.legalAuthoritiesCount || 16; // Default fallback
          
          set({
            isConnected: true,
            legalAuthoritiesCount: count,
            lastChecked: new Date(),
            message: "Command Legacy Beta: We don’t track, we back.",
            type: "success",
            icon: "✓"
          });
        } else {
          throw new Error(`Backend responded with status: ${response.status}`);
        }
      } catch (error) {
        set({
          isConnected: false,
          legalAuthoritiesCount: 0,
          lastChecked: new Date(),
          message: "System Maintenance - Please try again later",
          type: "warning",
          icon: "⚠"
        });
      }
    },

    updateConnectionStatus: (isConnected: boolean, count?: number) => {
      set({
        isConnected,
        legalAuthoritiesCount: count || 16,
        lastChecked: new Date(),
        message: "Command Legacy Beta: We don’t track, we back.",
        type: "success",
        icon: "✓"
      });
    },
  },
}));

// Export the color helper function
export { getColorForType };
